// Presentation rules for a credential's state. Pure and separately testable: this repo's babel is
// preset-env only (no TypeScript preset), so keep it plain JS with no annotations or jest cannot
// parse it.

// Maps the API's `status` plus the failure code onto what a user should see. The API's own strings
// are internal vocabulary ("needs_attention"), never shown raw.
export const CREDENTIAL_STATUS = {
  ACTIVE: 'Active',
  NEEDS_ATTENTION: 'Needs attention',
  DISABLED: 'Disabled',
  UNVERIFIED: 'Not yet verified'
};

// Order matters: disabled beats everything (an explicitly disabled credential is not "active" just
// because it last succeeded), and a failure beats a stale success.
export const credentialStatusLabel = credential => {
  if (!credential) return null;
  if (credential.disabledOn) return CREDENTIAL_STATUS.DISABLED;
  if (credential.lastFailureCode) return CREDENTIAL_STATUS.NEEDS_ATTENTION;
  if (credential.lastVerifiedAt || credential.lastSucceededAt) return CREDENTIAL_STATUS.ACTIVE;
  return CREDENTIAL_STATUS.UNVERIFIED;
};
export const credentialStatusVariant = credential => {
  switch (credentialStatusLabel(credential)) {
    case CREDENTIAL_STATUS.ACTIVE:
      return 'success';
    // Names must be slots defined in src/styles/components/chip.ts. 'error' and 'default' are not,
    // so the two states that most need to stand out rendered unstyled.
    case CREDENTIAL_STATUS.NEEDS_ATTENTION:
      return 'critical';
    case CREDENTIAL_STATUS.DISABLED:
      return 'inactive';
    default:
      return 'warning';
  }
};

// Failure codes are internal. Anything unrecognised must fall back to a generic message rather than
// leaking a raw code - some carry provider wording that can hint at the credential itself.
const SAFE_REASONS = {
  credential_failure: 'The provider rejected the saved login.',
  password_expired: 'The provider requires a password reset.',
  mfa_required: 'The provider requires multi-factor verification.',
  captcha_blocked: 'The provider challenged automated access.',
  infrastructure_failure: 'The provider could not be reached.'
};
export const credentialFailureMessage = credential => {
  if (!(credential !== null && credential !== void 0 && credential.lastFailureCode)) return null;
  return SAFE_REASONS[credential.lastFailureCode] || 'The last check did not succeed.';
};

// A credential belongs to this utility row only if the API said so. Never fall back to "same
// provider" - that would show one property's credential on another property's utility.
export const findCredentialForUtility = (credentials, propertyUtilityId) => {
  if (!(credentials !== null && credentials !== void 0 && credentials.length) || !propertyUtilityId) return null;
  return credentials.find(c => c.propertyUtilityId === propertyUtilityId) || null;
};

// Property utilities come back from more than one endpoint and the provider/type ids sit at
// different depths depending on which. Read both shapes rather than assuming one.
const providerIdOf = utility => {
  var _utility$provider;
  return (utility === null || utility === void 0 || (_utility$provider = utility.provider) === null || _utility$provider === void 0 ? void 0 : _utility$provider.utilityProviderId) || (utility === null || utility === void 0 ? void 0 : utility.utilityProviderId) || null;
};
const utilityTypeIdsOf = utility => ((utility === null || utility === void 0 ? void 0 : utility.utilityTypes) || []).map(type => (type === null || type === void 0 ? void 0 : type.utilityTypeId) || (type === null || type === void 0 ? void 0 : type.id)).filter(Boolean);

/**
 * Find the property utility that a just-completed ADD flow created.
 *
 * The create endpoints do not reliably return the new id, so it is resolved by re-reading the
 * property's utilities and matching what the user chose. Returns null rather than guessing when the
 * match is ambiguous: the caller must never write a credential against the wrong utility, and a
 * credential with no propertyUtilityId could never be found again.
 */
export const findCreatedPropertyUtility = function (utilities) {
  let _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    utilityProviderId = _ref.utilityProviderId,
    utilityTypeId = _ref.utilityTypeId;
  if (!Array.isArray(utilities) || !utilities.length || !utilityProviderId) return null;
  const sameProvider = utilities.filter(u => providerIdOf(u) === utilityProviderId && (u === null || u === void 0 ? void 0 : u.propertyUtilityId));
  if (!sameProvider.length) return null;
  if (utilityTypeId) {
    const sameType = sameProvider.filter(u => utilityTypeIdsOf(u).includes(utilityTypeId));
    if (sameType.length === 1) return sameType[0];
    // More than one utility for the same provider and type means the property already had one
    // before this save. Picking either is a coin flip, so decline.
    if (sameType.length > 1) return null;
    // Zero is not a "no": the list endpoint returns utilityTypes empty for a freshly created
    // utility. The type is a disambiguator, not a requirement, so fall through to the
    // provider-only rule below, which still refuses to choose between candidates.
  }
  return sameProvider.length === 1 ? sameProvider[0] : null;
};

/**
 * Is this provider covered by a bill scraper?
 *
 * `scraperProviderKey` is the only valid signal - never the display name, which produced
 * `burbank water power (bwp)` while the scraper leases `burbank`, and let a custom provider turn
 * free text into a key. Takes several shapes because the add path has no property utility yet.
 */
export const isScraperSupported = function () {
  for (var _len = arguments.length, providerLikes = new Array(_len), _key = 0; _key < _len; _key++) {
    providerLikes[_key] = arguments[_key];
  }
  return providerLikes.some(p => typeof (p === null || p === void 0 ? void 0 : p.scraperProviderKey) === 'string' && p.scraperProviderKey.length > 0);
};